import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { toDataURL } from 'qrcode';
import { environment } from '@et/environment';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { SetTwoFactorEnabled } from '@et/guards';
import { TwoFactorEnrollmentService } from '../../services/two-factor-enrollment.service';
import { AuthService } from '../../services/auth.service';

/**
 * Full-page, non-dismissible mandatory Authenticator-app enrollment gate.
 * `mfaRequiredGuard` (`@et/guards`) redirects here when
 * `SelfProfile.requiredMfaMethod === 'Authenticator'` and Better Auth's
 * `user.twoFactorEnabled` is still `false` — i.e. the admin has required TOTP for
 * this user/company but they haven't actually scanned a QR code yet. There is no
 * skip/dismiss action; the only way out is completing enrollment.
 *
 * Shares the same `TwoFactorEnrollmentService` HTTP calls as
 * `TwoFactorSettingsComponent` (the self-service `/app/profile/settings` variant)
 * so the enable/verify wiring lives in one place. Deliberately omits
 * disable/regenerate-backup-codes — this page only ever moves a user from
 * not-enrolled to enrolled.
 */
@Component({
  selector: 'auth-two-factor-required',
  templateUrl: './two-factor-required.component.html',
  standalone: true,
  imports: [PresentationalAtomsModule],
})
export class TwoFactorRequiredComponent {
  private enrollment = inject(TwoFactorEnrollmentService);
  private authService = inject(AuthService);
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  pendingActivation = false;
  activationMethod: 'totp' | 'otp' = 'totp';
  backupCodes: string[] = [];
  totpURI?: string;
  qrDataUrl?: string;
  error = false;
  submitting = false;
  totpEnabled = environment.betterAuth?.totpEnabled ?? false;

  async start(password: string): Promise<void> {
    this.error = false;
    this.submitting = true;
    try {
      const res = await this.enrollment.enable(password);
      this.backupCodes = res?.backupCodes ?? [];
      this.totpURI = res?.totpURI;
      this.qrDataUrl = this.totpURI ? await toDataURL(this.totpURI) : undefined;
      this.pendingActivation = true;
    } catch {
      this.error = true;
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Completes activation. `code` is a live TOTP code (authenticator app) by
   * default, or the emailed/SMS OTP when `activationMethod === 'otp'`.
   */
  async confirmActivation(code: string): Promise<void> {
    this.error = false;
    this.submitting = true;
    try {
      if (this.activationMethod === 'otp') {
        await this.enrollment.verifyOtp(code);
      } else {
        await this.enrollment.verifyTotp(code);
      }
      await this.onActivated();
    } catch {
      this.error = true;
    } finally {
      this.submitting = false;
    }
  }

  /** For `activationMethod === 'otp'`: requests the emailed/SMS code to confirm with. */
  async sendActivationOtp(): Promise<void> {
    this.error = false;
    try {
      await this.enrollment.sendOtp();
    } catch {
      this.error = true;
    }
  }

  /**
   * Re-reads Better Auth's live session so the store's `twoFactorEnabled` slice
   * reflects reality, then lets `mfaRequiredGuard` re-evaluate by navigating to
   * wherever the user originally intended to go.
   */
  private async onActivated(): Promise<void> {
    const session = await firstValueFrom(this.authService.getSession());
    this.store.dispatch(
      new SetTwoFactorEnabled(session?.twoFactorEnabled ?? true),
    );
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    void this.router.navigateByUrl(returnUrl || '/');
  }
}
